import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { TitleWithTooltipColumns } from 'components/TitleWithTooltipColumns';
import type { WorkOrderOtherPriceData, WorkOrderPriceStatusEnum } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { percentCellFormatter } from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';
import { badgePropsByStatus } from 'pages/Project/Common/CodeCell';
import { getNumberFormatter } from 'pages/Project/Common/utils';
import { WorkOrderPriceCodeCell } from 'pages/Project/UerProject/WorkOrder/WorkOrderPrice/WorkOrderPriceCodeCell';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo } from 'react';

import { destroyWorkOrderFilters } from '../../WorkOrderPrice/hooks';
import { OtherAgMenuCellRendererComponent } from '../components/OtherAgMenuCellRendererComponent';
import { OtherStatusCellRendererComponent } from '../components/OtherStatusCellRendererComponent';
import type { rowsWithGroupTitle } from '../otherHelpers';
import { getOrderOtherNameFilterItems, getOrderOtherStatusFilterItems } from '../workOrderOtherTableQueries';

const titleWithTooltipColumnsTotalPlanCost = () => (
    <TitleWithTooltipColumns
        title="Стоимость всего, руб. без НДС"
        childTooltip="Стоимость всего в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС"
    />
);

export const useWorkOrderOtherColumns = ({
    workOrderStatus,
    checkedIds,
    handleChangeCheckbox,
    isContractStatusWork,
    isDisabledByProjectStatus,
    hasActs,
    seasonAndLinkedActsColumns,
    changeFilterHandler,
    gridApiRef,
    workOrderId,
    workOrderObjectId,
    costRounding,
}: {
    workOrderStatus: WorkOrderStatusEnum | undefined;
    checkedIds: Record<string, boolean>;
    handleChangeCheckbox: (rowId: number) => ({ checked }: { checked: boolean }) => void;
    isContractStatusWork: boolean;
    isDisabledByProjectStatus: boolean | undefined;
    hasActs: boolean;
    seasonAndLinkedActsColumns: (ColDef<rowsWithGroupTitle> | ColGroupDef<rowsWithGroupTitle>)[];
    changeFilterHandler: ({ fieldName, checkedValues }: { fieldName: string; checkedValues: { data: any } }) => void;
    gridApiRef: AgGridReact<any> | null;
    workOrderId: number;
    workOrderObjectId: number;
    costRounding: number;
}) => {
    const filterVariables = useMemo(
        () => ({ workOrderId, workOrderOtherPriceObjectId: workOrderObjectId }),
        [workOrderId, workOrderObjectId],
    );

    const columns = useMemo<(ColDef<rowsWithGroupTitle> | ColGroupDef<rowsWithGroupTitle>)[]>(
        () => [
            {
                headerName: '',
                cellRenderer: OtherAgMenuCellRendererComponent,
                cellRendererParams: {
                    checkedIds,
                    handleChangeCheckbox,
                    isContractStatusWork,
                    isDisabledByProjectStatus,
                    workOrderStatus,
                    hasActs,
                },
                equals: () => false,
                field: 'menu' as any,
                minWidth: 80,
                maxWidth: 80,
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'centeredAgGridCell',
            },
            {
                headerName: 'Статус',
                field: 'status',
                minWidth: 110,
                maxWidth: 110,
                cellRenderer: OtherStatusCellRendererComponent,
                cellClass: 'leftAgGridCell',
                tooltipComponent: undefined,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderOtherPriceStatusFilter',
                    query: getOrderOtherStatusFilterItems,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Статус',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({ value }: { value: WorkOrderPriceStatusEnum }) =>
                                value ? badgePropsByStatus[value].label : '',
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Шифр',
                field: 'code' as any,
                minWidth: 80,
                maxWidth: 80,
                cellClass: 'centeredAgGridCell',
                cellRenderer: ({ data }: { data: WorkOrderOtherPriceData }) => (
                    <WorkOrderPriceCodeCell row={data} workOrderStatus={workOrderStatus} />
                ),
            },
            {
                headerName: 'Наименование',
                field: 'name',
                minWidth: 400,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderOtherPriceNameFilter',
                    query: getOrderOtherNameFilterItems,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед. изм.',
                valueFormatter: (data) => percentCellFormatter(data, 2),
                field: 'calculatingTypeValue',
                cellClass: 'centeredAgGridCell',
                minWidth: 80,
                maxWidth: 120,
            },
            {
                headerName: 'План по НЗ',
                headerClass: 'flexStart',
                field: 'plan' as any,
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalPlanVolume' as any,
                        cellClass: 'ag-right-aligned-cell boldCell',
                        width: 125,
                    },
                    {
                        headerComponent: titleWithTooltipColumnsTotalPlanCost,
                        field: 'totalPlanCost',
                        cellClass: 'ag-right-aligned-cell boldCell',
                        width: 160,
                        valueFormatter: getNumberFormatter(costRounding || 0),
                    },
                ],
            },
            ...(workOrderStatus === WorkOrderStatusEnum.Completed || workOrderStatus === WorkOrderStatusEnum.InProgress
                ? [
                      {
                          headerName: 'Выполнено по нз',
                          marryChildren: true,
                          headerClass: 'flexStart',
                          children: [
                              {
                                  headerName: 'Объем всего',
                                  field: 'totalCompletedVolume' as any,
                                  cellClass: 'ag-right-aligned-cell',
                                  width: 125,
                              },
                              {
                                  headerName: 'Стоимость всего, руб. без НДС',
                                  headerComponent: titleWithTooltipColumnsTotalPlanCost,
                                  field: 'totalCompletedCost' as any,
                                  cellClass: 'ag-right-aligned-cell',
                                  width: 160,
                                  valueFormatter: getNumberFormatter(costRounding || 0),
                              },
                          ],
                      },
                      {
                          headerName: 'Остаток по нз',
                          headerClass: 'flexStart',
                          marryChildren: true,
                          children: [
                              {
                                  headerName: 'Объем всего',
                                  field: 'totalRemainderVolume',
                                  cellClass: 'ag-right-aligned-cell',
                                  width: 125,
                              },
                              {
                                  headerName: 'Стоимость всего, руб. без НДС',
                                  headerComponent: titleWithTooltipColumnsTotalPlanCost,
                                  field: 'totalRemainderCost',
                                  cellClass: 'ag-right-aligned-cell',
                                  width: 160,
                                  valueFormatter: getNumberFormatter(costRounding || 0),
                              },
                          ],
                      },
                  ]
                : []),
            ...seasonAndLinkedActsColumns,
        ],
        [
            changeFilterHandler,
            checkedIds,
            costRounding,
            filterVariables,
            handleChangeCheckbox,
            hasActs,
            isContractStatusWork,
            isDisabledByProjectStatus,
            seasonAndLinkedActsColumns,
            workOrderStatus,
        ],
    );

    const [selection, , , setSelection] = useSelection('workOrderOtherPriceTable');
    const [pinnedItems, , , setPinned] = usePin('workOrderOtherPriceTable');

    const isColumnVisible = useCallback((id: string) => selection[id] ?? true, [selection]);

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 6) {
                        newColumn.disabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [columns, pinnedItems],
    ) as any[];

    const handleTableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setPinned,
                setSelection,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );

    useEffect(() => {
        destroyWorkOrderFilters({
            api: gridApiRef?.api,
            columns: ['name', 'status'],
        });
    }, [filterVariables, gridApiRef?.api]);

    return {
        fullSetColumns,
        fullSetSettingsColumns,
        handleTableSettingsConfirm,
        isColumnVisible,
    };
};
